/**
 * Canonical realtime event names — the SINGLE source of truth shared with the
 * backend (`src/realtime/MIGRATION.md`). Clients listen by the event name; the
 * channel/scope is handled by the gateway (auto-subscribed) so features only
 * care about the event.
 *
 * Keep these strings byte-identical to the backend — a typo silently means
 * "never fires".
 */
export const RealtimeEvents = {
  // ── Task collaboration (business-scoped) ───────────────────────────────────
  TASK_CREATED: 'task.created',
  TASK_UPDATED: 'task.updated',
  TASK_COMMENTED: 'task.commented',
  TASK_STATUS_UPDATED: 'task.statusUpdated',
  TASK_SUB_STATUS_UPDATED: 'task.subStatusUpdated',
  TASK_SUBTASK_CREATED: 'task.subTaskCreated',
  TASK_ASSIGNEE_UPDATED: 'task.assigneeUpdated',

  // ── Attendance / timesheet ─────────────────────────────────────────────────
  ATTENDANCE_UPDATED: 'attendance.updated',                 // user-scoped
  CLOCKED_HISTORY_UPDATED: 'clockedHistory.updated',        // business-scoped
  ATTENDANCE_DASHBOARD_UPDATE: 'attendance.dashboardUpdate',// business-scoped
  ATTENDANCE_NUMBER_INCREMENTED: 'attendance.numberIncremented', // business-scoped

  // ── Scheduler ──────────────────────────────────────────────────────────────
  SCHEDULE_CSV_PROGRESS: 'schedule.csvProgress',           // user-scoped   { totalEmployees, doneEmployees, ... }

  // ── Notifications ──────────────────────────────────────────────────────────
  NOTIFICATION_CREATED: 'notification.created',             // user-scoped   <notification>
  NOTIFICATION_COUNT: 'notification.count',                 // user-scoped   { notificationCount }

  // ── Account / permissions / business settings ──────────────────────────────
  PERMISSIONS_CHANGED: 'permissions.changed',               // user-scoped   { is_role_changed, is_password_changed, is_navigated_business_change }
  BUSINESS_TIMEZONE_CHANGED: 'business.timezoneChanged',    // business-scoped { is_*_change }

  // ── Employee / business ────────────────────────────────────────────────────
  BUSINESS_USER_INFO_UPDATED: 'business.userInfoUpdated',   // business-scoped
  ACCOUNT_STATUS_CHANGED: 'account.statusChanged',          // user-scoped   { account_status }

  // ── Document control ───────────────────────────────────────────────────────
  DOCUMENT_UPLOAD_PROGRESS: 'document.uploadProgress',      // user-scoped   { percent }
  DOCUMENT_ZIP_DOWNLOAD: 'document.zipDownload',            // user-scoped   { percent }
  DOCUMENT_DELETED: 'document.deleted',                     // business-scoped
  DOCUMENT_SHARED: 'document.shared',                       // user-scoped
  DOCUMENT_ASSIGNED: 'document.assigned',                   // user-scoped
} as const;

export type RealtimeEventName = (typeof RealtimeEvents)[keyof typeof RealtimeEvents];

/** All task events — convenient for "any task changed → reload the board". */
export const TASK_EVENTS: readonly RealtimeEventName[] = [
  RealtimeEvents.TASK_CREATED,
  RealtimeEvents.TASK_UPDATED,
  RealtimeEvents.TASK_COMMENTED,
  RealtimeEvents.TASK_STATUS_UPDATED,
  RealtimeEvents.TASK_SUB_STATUS_UPDATED,
  RealtimeEvents.TASK_SUBTASK_CREATED,
  RealtimeEvents.TASK_ASSIGNEE_UPDATED,
];
